/**
 * Cameras, microphones, and the ways asking for one goes wrong.
 *
 * Plain `navigator.mediaDevices`, no LiveKit. The lobby runs this and the
 * lobby must not download a WebRTC SDK to show you your own face.
 */

import * as React from 'react'

/* -------------------------------------------------------------------------- */
/* what went wrong                                                             */
/* -------------------------------------------------------------------------- */

export type MediaProblemKind =
  | 'denied'
  | 'missing'
  | 'busy'
  | 'insecure'
  | 'unsupported'
  | 'overconstrained'
  | 'other'

export interface MediaProblem {
  kind: MediaProblemKind
  title: string
  detail: string
  /**
   * Whether pressing "try again" can plausibly succeed without the person
   * doing something outside the page first.
   *
   * `denied` is false: the browser will not re-prompt for an origin that has
   * been refused, so a retry button that silently fails is worse than a
   * sentence telling you where the setting is.
   */
  retryable: boolean
}

/**
 * getUserMedia rejects with a `DOMException` whose `name` is the only reliable
 * discriminator — the `message` is browser-specific prose. Mapping it here
 * means every surface in the app explains the same failure the same way.
 *
 * `NotAllowedError` is by far the most common and is the one people get stuck
 * on, because Chrome's second refusal is silent: the promise rejects with no
 * prompt at all, so a UI that just says "failed" looks broken rather than
 * blocked.
 */
export function describeMediaProblem(error: unknown): MediaProblem {
  if (!hasMediaDevices()) {
    return isSecureContextForMedia()
      ? {
          kind: 'unsupported',
          title: 'This browser cannot open a camera',
          detail:
            'navigator.mediaDevices is missing. A current Chrome, Edge, Firefox or Safari will work.',
          retryable: false,
        }
      : {
          kind: 'insecure',
          title: 'This page is not on a secure origin',
          detail:
            'Browsers only hand out a camera or microphone over HTTPS, or on localhost. Opening this app by IP address over plain HTTP disables media entirely.',
          retryable: false,
        }
  }

  const name = error instanceof DOMException ? error.name : ''

  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return {
        kind: 'denied',
        title: 'Camera and microphone are blocked',
        detail:
          'The browser refused the request for this site. Open the padlock or camera icon in the address bar, set camera and microphone to Allow, then reload.',
        retryable: false,
      }
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return {
        kind: 'missing',
        title: 'No camera or microphone found',
        detail:
          'Nothing is plugged in that the browser can see. You can still join and listen — you just will not be heard.',
        retryable: true,
      }
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return {
        kind: 'busy',
        title: 'The device is in use',
        detail:
          'Another application already holds the camera or microphone. Close it — video calls in other tabs count — and try again.',
        retryable: true,
      }
    case 'OverconstrainedError':
      return {
        kind: 'overconstrained',
        title: 'That device would not start',
        detail:
          'The camera or microphone you picked no longer accepts these settings. Choosing a different one usually clears it.',
        retryable: true,
      }
    default:
      return {
        kind: 'other',
        title: 'Could not open the camera or microphone',
        detail: error instanceof Error && error.message ? error.message : 'An unknown media error.',
        retryable: true,
      }
  }
}

export function hasMediaDevices(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
}

export function canShareScreen(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia
}

/** `window.isSecureContext`, with the localhost exemption spelled out. */
export function isSecureContextForMedia(): boolean {
  if (typeof window === 'undefined') return false
  return window.isSecureContext === true
}

/* -------------------------------------------------------------------------- */
/* devices                                                                     */
/* -------------------------------------------------------------------------- */

export interface DeviceOption {
  deviceId: string
  label: string
}

export interface DeviceInventory {
  cameras: DeviceOption[]
  microphones: DeviceOption[]
  speakers: DeviceOption[]
  /**
   * True once at least one device has a real label. Labels are withheld until
   * a stream has been granted, so before that the pickers can only offer
   * "Microphone 1" — which is why the preview asks for permission first and
   * enumerates second.
   */
  labelled: boolean
}

const EMPTY_INVENTORY: DeviceInventory = {
  cameras: [],
  microphones: [],
  speakers: [],
  labelled: false,
}

/**
 * The device list, kept current.
 *
 * `devicechange` fires when a headset is plugged in or a webcam is unplugged
 * mid-call, and it is the difference between "the picker is a snapshot from
 * page load" and "the picker is right". `revision` is the caller's way of
 * forcing a re-read after a successful `getUserMedia`, at which point the OS
 * finally gives up the labels.
 */
export function useDeviceInventory(revision: number): DeviceInventory {
  const [inventory, setInventory] = React.useState<DeviceInventory>(EMPTY_INVENTORY)

  React.useEffect(() => {
    if (!hasMediaDevices()) return
    let live = true

    const read = async () => {
      let devices: MediaDeviceInfo[]
      try {
        devices = await navigator.mediaDevices.enumerateDevices()
      } catch {
        return
      }
      if (!live) return
      setInventory(toInventory(devices))
    }

    void read()
    navigator.mediaDevices.addEventListener('devicechange', read)
    return () => {
      live = false
      navigator.mediaDevices.removeEventListener('devicechange', read)
    }
  }, [revision])

  return inventory
}

function toInventory(devices: MediaDeviceInfo[]): DeviceInventory {
  const pick = (kind: MediaDeviceKind): DeviceOption[] =>
    devices
      .filter((device) => device.kind === kind && device.deviceId !== '')
      .map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `${friendlyKind(kind)} ${index + 1}`,
      }))

  return {
    cameras: pick('videoinput'),
    microphones: pick('audioinput'),
    speakers: pick('audiooutput'),
    labelled: devices.some((device) => device.label !== ''),
  }
}

function friendlyKind(kind: MediaDeviceKind): string {
  if (kind === 'videoinput') return 'Camera'
  if (kind === 'audioinput') return 'Microphone'
  return 'Speaker'
}

/**
 * `default` and `communications` are the same physical device under two ids on
 * Windows, and listing both makes the picker look broken. This drops the
 * aliases when a real entry for the same group exists.
 */
export function dedupeDevices(options: DeviceOption[]): DeviceOption[] {
  const seen = new Set<string>()
  const out: DeviceOption[] = []
  for (const option of options) {
    const key = option.label.replace(/^(Default|Communications)\s*-\s*/i, '').trim().toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(option)
  }
  return out
}

/* -------------------------------------------------------------------------- */
/* remembered choices                                                          */
/* -------------------------------------------------------------------------- */

const DEVICE_KEY = 'era.meet.devices'

export interface DevicePreferences {
  camera: string | null
  microphone: string | null
  speaker: string | null
}

const NO_PREFERENCES: DevicePreferences = { camera: null, microphone: null, speaker: null }

/**
 * Which device you picked last time.
 *
 * Device ids are stable per origin per browser profile, so this survives
 * reloads and is meaningless anywhere else — which is fine, because a device
 * id that no longer resolves simply falls back to the system default rather
 * than failing.
 */
export function readDevicePreferences(): DevicePreferences {
  try {
    const raw = localStorage.getItem(DEVICE_KEY)
    if (!raw) return NO_PREFERENCES
    const parsed = JSON.parse(raw) as Partial<DevicePreferences>
    return {
      camera: typeof parsed.camera === 'string' ? parsed.camera : null,
      microphone: typeof parsed.microphone === 'string' ? parsed.microphone : null,
      speaker: typeof parsed.speaker === 'string' ? parsed.speaker : null,
    }
  } catch {
    return NO_PREFERENCES
  }
}

export function writeDevicePreferences(preferences: DevicePreferences): void {
  try {
    localStorage.setItem(DEVICE_KEY, JSON.stringify(preferences))
  } catch {
    /* the pickers just start at the system default next time */
  }
}

/* -------------------------------------------------------------------------- */
/* the clock                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Milliseconds since `since`, ticking once a second.
 *
 * Driven by an interval rather than by `requestAnimationFrame`: the call timer
 * changes once a second and a rAF loop would re-render the entire call tree at
 * 60 Hz to move one digit.
 */
export function useElapsed(since: number | null): number {
  const [now, setNow] = React.useState(() => Date.now())

  React.useEffect(() => {
    if (since === null) return
    setNow(Date.now())
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [since])

  return since === null ? 0 : Math.max(0, now - since)
}
